/**
 * Ambience bed.
 *
 *  - wind: looped brown/pink noise through a pair of slowly modulated bandpasses,
 *    stereo-decorrelated, gusting on a seeded random walk
 *  - distant city: heavily low-passed brown rumble with occasional swells
 *  - distant gunfire: seeded, sparse, using the real distant-gunshot voicing
 *  - birds: short FM chirps, outdoors only, silenced during combat
 *  - room tone: a quiet indoor hum that fades up as the zone mix goes indoors
 *
 * The whole bed crossfades between an outdoor and an indoor balance driven by the
 * zone system.
 */
import { makeRng } from '../core/Rand.js';
import { gain, bq, playData } from './synth/nodes.js';
import { noiseSlice, bedBuffer } from './synth/noise.js';
import { applyAD, bandpass, normalise as normArr, TAU } from './synth/dsp.js';

export class Ambience {
  /**
   * @param {BaseAudioContext} ac
   * @param {import('./Buses.js').Buses} buses
   */
  constructor(ac, buses, { seed = 0xa3b1e } = {}) {
    this.ac = ac;
    this.buses = buses;
    this.rng = makeRng(seed);
    this.out = gain(ac, 1);
    this.out.connect(buses.input('world'));

    this.indoor = 0;
    this.combat = 0;
    this.intensity = 1;
    this._nodes = [];
    this._sources = [];

    this._buildWind();
    this._buildCity();

    this.nextBird = ac.currentTime + 2 + this.rng() * 6;
    this.nextGunfire = ac.currentTime + 6 + this.rng() * 14;
    this.gunfireCb = null;
  }

  _buildWind() {
    const ac = this.ac;
    this.windGain = gain(ac, 0.0);
    this.windBP = bq(ac, 'bandpass', 480, 0.55);
    this.windLP = bq(ac, 'lowpass', 2400, 0.7);
    const merger = ac.createChannelMerger(2);

    for (let c = 0; c < 2; c++) {
      const src = ac.createBufferSource();
      src.buffer = bedBuffer(ac, c === 0 ? 'brown' : 'pink');
      src.loop = true;
      src.playbackRate.value = c === 0 ? 0.83 : 0.97;
      const g = gain(ac, c === 0 ? 0.8 : 0.55);
      const bp = bq(ac, 'bandpass', c === 0 ? 380 : 620, 0.5);
      src.connect(bp).connect(g).connect(merger, 0, c);
      this._sources.push(src);
      this._nodes.push(g, bp);
    }
    merger.connect(this.windLP).connect(this.windGain).connect(this.out);

    // gust LFOs — two incommensurate rates so the gusting never repeats audibly
    this.gustA = ac.createOscillator();
    this.gustB = ac.createOscillator();
    this.gustA.type = 'sine'; this.gustB.type = 'sine';
    this.gustA.frequency.value = 0.037;
    this.gustB.frequency.value = 0.0913;
    const ga = gain(ac, 0.05);
    const gb = gain(ac, 0.03);
    this.gustA.connect(ga).connect(this.windGain.gain);
    this.gustB.connect(gb).connect(this.windGain.gain);
    const fa = gain(ac, 900);
    this.gustA.connect(fa).connect(this.windLP.frequency);
    this._sources.push(this.gustA, this.gustB);
    this._nodes.push(ga, gb, fa, merger, this.windLP, this.windGain, this.windBP);
  }

  _buildCity() {
    const ac = this.ac;
    this.cityGain = gain(ac, 0);
    const src = ac.createBufferSource();
    src.buffer = bedBuffer(ac, 'brown');
    src.loop = true;
    src.playbackRate.value = 0.61;
    const lp = bq(ac, 'lowpass', 190, 0.8);
    const hp = bq(ac, 'highpass', 42, 0.7);
    src.connect(lp).connect(hp).connect(this.cityGain).connect(this.out);
    this._sources.push(src);
    this._nodes.push(lp, hp, this.cityGain);

    // indoor room tone: a narrow low hum plus a faint hiss
    this.roomGain = gain(ac, 0);
    const hum = ac.createOscillator();
    hum.type = 'sine';
    hum.frequency.value = 51;
    const humG = gain(ac, 0.35);
    hum.connect(humG).connect(this.roomGain);
    const hiss = ac.createBufferSource();
    hiss.buffer = bedBuffer(ac, 'pink');
    hiss.loop = true;
    hiss.playbackRate.value = 1.13;
    const hissBP = bq(ac, 'bandpass', 3200, 0.7);
    const hissG = gain(ac, 0.06);
    hiss.connect(hissBP).connect(hissG).connect(this.roomGain);
    this.roomGain.connect(this.out);
    this._sources.push(hum, hiss);
    this._nodes.push(humG, hissBP, hissG, this.roomGain);
  }

  start() {
    const t = this.ac.currentTime;
    for (const s of this._sources) { try { s.start(t); } catch { /* already running */ } }
    this.setIndoor(0);
  }

  /** 0 = fully outdoors, 1 = fully enclosed. */
  setIndoor(v) {
    this.indoor = Math.max(0, Math.min(1, v));
    const t = this.ac.currentTime;
    const i = this.indoor;
    this.windGain.gain.setTargetAtTime((0.055 * (1 - i * 0.82)) * this.intensity, t, 1.2);
    this.cityGain.gain.setTargetAtTime((0.075 * (1 - i * 0.6)) * this.intensity, t, 1.2);
    this.roomGain.gain.setTargetAtTime(0.030 * i * this.intensity, t, 1.2);
    this.windLP.frequency.setTargetAtTime(2400 - 1700 * i, t, 1.0);
  }

  setIntensity(v) {
    this.intensity = Math.max(0, Math.min(1.5, v));
    this.setIndoor(this.indoor);
  }

  /** Called every frame. Schedules sparse one-shot ambience events. */
  update(dt) {
    const now = this.ac.currentTime;
    if (now >= this.nextBird) {
      this.nextBird = now + 4 + this.rng() * 14;
      if (this.indoor < 0.4 && this.combat < 0.3) this._bird();
    }
    if (now >= this.nextGunfire) {
      this.nextGunfire = now + (this.combat > 0.3 ? 1.5 + this.rng() * 5 : 8 + this.rng() * 22);
      if (this.gunfireCb) { try { this.gunfireCb(); } catch { /* never break the frame */ } }
    }
  }

  /** A short two- or three-note FM chirp. */
  _bird() {
    const ac = this.ac;
    const sr = ac.sampleRate;
    const rng = this.rng;
    const notes = 2 + Math.floor(rng() * 3);
    const base = 2600 + rng() * 2200;
    let t = ac.currentTime + rng() * 0.3;
    const pan = ac.createStereoPanner ? ac.createStereoPanner() : null;
    let dest = this.out;
    if (pan) { pan.pan.value = rng() * 1.6 - 0.8; pan.connect(this.out); dest = pan; }
    for (let i = 0; i < notes; i++) {
      const dur = 0.05 + rng() * 0.07;
      const n = Math.floor(sr * dur);
      const d = new Float32Array(n);
      const f0 = base * (0.85 + rng() * 0.4);
      const f1 = f0 * (0.7 + rng() * 0.8);
      const mIdx = 0.4 + rng() * 1.2;
      let ph = 0;
      let mph = 0;
      for (let k = 0; k < n; k++) {
        const u = k / n;
        const f = f0 + (f1 - f0) * u;
        mph += (TAU * f * 2.7) / sr;
        ph += (TAU * f * (1 + mIdx * 0.06 * Math.sin(mph))) / sr;
        d[k] = Math.sin(ph) * Math.sin(Math.PI * u);
      }
      normArr(d, 1);
      const g = gain(ac, 0.020 * (0.5 + rng()) * (1 - this.indoor) * this.intensity);
      g.connect(dest);
      playData(ac, d, g, t, {});
      t += dur + 0.02 + rng() * 0.06;
    }
  }

  /** A short cloth/gear swish for player movement, driven from Audio.js. */
  movement(level = 0.5) {
    const ac = this.ac;
    const sr = ac.sampleRate;
    const n = Math.floor(sr * 0.24);
    const d = noiseSlice('pink', n, this.rng);
    bandpass(d, sr, 1500 + this.rng() * 900, 0.8);
    applyAD(d, sr, 0.05, 0.07);
    normArr(d, 1);
    const g = gain(ac, 0.05 * level * this.intensity);
    const hp = bq(ac, 'highpass', 600, 0.7);
    hp.connect(g).connect(this.out);
    playData(ac, d, hp, ac.currentTime + 0.001, {});
  }

  dispose() {
    const t = this.ac.currentTime;
    for (const s of this._sources) { try { s.stop(t + 0.05); } catch { /* ignore */ } }
    for (const n of this._nodes) { try { n.disconnect(); } catch { /* ignore */ } }
    try { this.out.disconnect(); } catch { /* ignore */ }
  }
}
